import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Send, Loader2, MessageSquare, ArrowLeft } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvos = () => http.get("/conversations").then((r) => setConvos(r.data)).catch((e) => toast.error(formatError(e)));

  const openThread = async (oid) => {
    setActive(oid);
    setThread(null);
    try {
      const { data } = await http.get(`/messages/${oid}`);
      setThread(data);
      loadConvos();
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { const to = params.get("to"); if (to) openThread(to); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.detail;
      loadConvos();
      if (active && d.from_id === active) {
        setThread((t) => (t ? { ...t, messages: [...t.messages, d] } : t));
        http.get(`/messages/${active}`).catch(() => {});
      }
    };
    window.addEventListener("pv:message", onMsg);
    return () => window.removeEventListener("pv:message", onMsg);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    const body = text.trim();
    setText("");
    try {
      const { data } = await http.post("/messages", { to_id: active, text: body });
      setThread((t) => (t ? { ...t, messages: [...t.messages, data] } : t));
      loadConvos();
    } catch (e) { toast.error(formatError(e)); setText(body); }
    finally { setSending(false); }
  };

  if (!convos) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin pv-text-dark" /></div>;

  return (
    <div className="max-w-4xl mx-auto" data-testid="messages-page">
      <h1 className="text-3xl font-black font-display pv-text-dark mb-4">Messages</h1>
      <div className="grid md:grid-cols-3 gap-4">
        <div className={`md:col-span-1 space-y-2 ${active ? "hidden md:block" : ""}`} data-testid="conversation-list">
          {convos.length === 0 && <div className="rounded-2xl bg-white border border-black/5 p-5 text-sm text-black/50">No conversations yet. Message a player from the Home screen to start one.</div>}
          {convos.map((c) => (
            <button key={c.other_id} data-testid={`conversation-${c.other_id}`} onClick={() => openThread(c.other_id)} className={`w-full text-left rounded-xl border border-black/5 bg-white px-3 py-3 flex items-center gap-3 hover:bg-black/[0.02] transition-colors ${active === c.other_id ? "ring-2 ring-[#BED445]" : ""}`}>
              <Avatar className="h-10 w-10"><AvatarImage src={c.avatar} /><AvatarFallback>{c.name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0"><div className="font-bold pv-text-dark text-sm truncate">{c.name}</div><div className="text-xs text-black/50 truncate">{c.last_text}</div></div>
              {c.unread > 0 && <span className="shrink-0 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-[#F5B700] text-black text-[10px] font-black">{c.unread}</span>}
            </button>
          ))}
        </div>

        <div className={`md:col-span-2 rounded-2xl bg-white border border-black/5 flex flex-col h-[65vh] ${!active ? "hidden md:flex" : "flex"}`} data-testid="message-thread">
          {!active ? (
            <div className="flex-1 grid place-items-center text-black/40"><div className="text-center"><MessageSquare className="mx-auto mb-2" /><div className="text-sm">Select a conversation</div></div></div>
          ) : !thread ? (
            <div className="flex-1 grid place-items-center"><Loader2 className="animate-spin pv-text-dark" /></div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-black/5 p-3">
                <button onClick={() => setActive(null)} className="md:hidden text-black/50"><ArrowLeft size={18} /></button>
                <Avatar className="h-9 w-9"><AvatarImage src={thread.other?.avatar} /><AvatarFallback>{thread.other?.name?.[0]}</AvatarFallback></Avatar>
                <div className="font-bold pv-text-dark">{thread.other?.name}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="thread-messages">
                {thread.messages.length === 0 && <div className="text-center text-sm text-black/40 mt-6">Say hi and set up a game.</div>}
                {thread.messages.map((m) => {
                  const mine = m.from_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#BED445] text-black" : "bg-black/[0.05] pv-text-dark"}`}>{m.text}</div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-black/5 p-3 flex items-center gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message…" className="flex-1 border rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#BED445]" />
                <button data-testid="message-send-btn" onClick={send} disabled={sending || !text.trim()} className="h-10 w-10 grid place-items-center rounded-full bg-[#0E395F] text-white disabled:opacity-50">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
